/**
 * The real API adapter: /api/v1, session cookie, x-csrf on mutations.
 *
 * This file was written against the plan and had never executed. It has now been reconciled with
 * what `packages/core/src/api/**` actually answers, and the eight differences are each marked
 * below with the reason the API's shape is the one that wins:
 *
 *  1. an error body is `{error:{code,message,issues?}}`, so `call()` reads `body.error.message`
 *     and the console shows the API's own sentence instead of "GET /inbox failed";
 *  2. a zod response schema has to be an object, so the list routes are ENVELOPED —
 *     `{messages}`, `{handoffs}`, `{handoff}`, `{products}`, `{product}`, `{metrics}`, `{users}`;
 *  3. `MetricValue.value` is `number | null` (see `formatMetric`, which prints "—");
 *  4. there is no `/leads/:id/takeover` — claiming a handoff is the take-over;
 *  5. there is no `/leads/:id/steer`;
 *  6. there is no `/theme` at all, so `theme()` below never issues a request;
 *  7. `lang` can be `"unknown"` — the API maps a NULL `convo.conversations.language` to it;
 *  8. `POST /leads/:id/stage` answers 409 on a backwards drag, which the Pipeline shows.
 *
 * Author: Danish Khan
 */
import type { Api } from './apiTypes.ts';
import { ApiError } from './apiTypes.ts';
import { DEFAULT_THEME, type ThemeJson } from './theme.ts';
import type {
  Contact,
  Conversation,
  Explain,
  Handoff,
  HandoffOutcome,
  HealthSummary,
  HomeSummary,
  InboxQuery,
  Lead,
  Message,
  MetricValue,
  Page,
  PipelineCard,
  PipelineStage,
  Product,
  Role,
  Session,
  User,
} from './types.ts';

const BASE = '/api/v1';

/**
 * The value every mutation has to echo back in `x-csrf`.
 *
 * It is held in module memory rather than in storage on purpose: it is refreshed by `login()` and
 * by `me()`, both of which run before any page can mutate, and a copy in localStorage is a copy
 * that outlives the session it belongs to.
 */
let csrf: string | null = null;

/** Exported for the event stream, which reconnects and needs to know there IS a session. */
export const currentCsrf = (): string | null => csrf;

interface ErrorBody {
  error?: { code?: string; message?: string; issues?: { path: string; message: string }[] };
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const headers = new Headers(init?.headers);
  if (method !== 'GET') {
    /**
     * `content-type: application/json` ONLY when there is a body, and this is not fussiness.
     *
     * `createApp` registers a raw content-type parser for `application/json` so the webhook can
     * keep Meta's exact bytes for the HMAC. It runs `JSON.parse("")` on a bodyless POST, throws,
     * and the app-level handler answers `400 bad request` as PLAIN TEXT — before the API router,
     * so not even in the `{error:{code,message}}` shape. Claim, release, logout and deactivate
     * all send no body, so with the header set unconditionally every one of them failed 400 and
     * the console had no sentence to show for it. Found in the browser, not in a test.
     */
    if (init?.body !== undefined) headers.set('content-type', 'application/json');
    if (csrf) headers.set('x-csrf', csrf);
  }
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'same-origin' });
  } catch (cause) {
    // A dead server is not a 500 — there is no response to read a code out of. Saying "the API
    // did not answer" is the difference between "restart the server" and "read the logs".
    throw new ApiError(`the API did not answer — is the server running on this origin? (${method} ${path})`, 0, {
      cause: String(cause),
    });
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // An HTML error page from a proxy, or a stream that was not JSON. Keep the first line.
    if (!res.ok) throw new ApiError(text.slice(0, 200) || `${method} ${path} failed`, res.status);
  }
  if (!res.ok) {
    const detail = (body ?? {}) as ErrorBody & Record<string, unknown>;
    // MISMATCH 1: `{error: {code, message}}`, not `{message}`.
    const message = typeof detail.error?.message === 'string'
      ? detail.error.message
      : `${method} ${path} failed`;
    throw new ApiError(message, res.status, {
      ...(detail.error?.code ? { code: detail.error.code } : {}),
      ...(detail.error?.issues ? { issues: detail.error.issues } : {}),
    });
  }
  return body as T;
}

function qs(params: Record<string, string | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, v);
  const s = search.toString();
  return s ? `?${s}` : '';
}

const json = (value: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(value) });
const post = (): RequestInit => ({ method: 'POST' });

export const httpApi: Api = {
  kind: 'http',

  async login(email, password) {
    const session = await call<Session>('/auth/login', json({ email, password }));
    csrf = session.csrf;
    return session;
  },

  async logout() {
    await call<{ ok: true }>('/auth/logout', post());
    csrf = null;
  },

  async me() {
    try {
      const session = await call<Session>('/auth/me');
      csrf = session.csrf;
      return session;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        csrf = null;
        return null;
      }
      throw err;
    }
  },

  /**
   * MISMATCH 6: there is no `/theme` route, so nothing is requested.
   *
   * There is no `theme.json` in any pack and the pack schema is not this task's to change, so the
   * honest handling is the house palette — which is what a tenant without a theme should get
   * anyway. Returning it synchronously rather than fetching and swallowing a 404 is why no page
   * load carries a failed request for a route the API has never had.
   */
  async theme(): Promise<ThemeJson> {
    return DEFAULT_THEME;
  },

  // MISMATCH 2 + 3: enveloped, and every `value` may be null.
  metrics: async (names) =>
    (await call<{ metrics: MetricValue[] }>(`/metrics${qs({ names: names?.join(',') })}`)).metrics,

  home: () => call<HomeSummary>('/home'),

  inbox: (query: InboxQuery) =>
    call<Page<Conversation>>(
      `/inbox${qs({
        view: query.view ?? null,
        tier: query.tier === 'all' ? null : (query.tier ?? null),
        lang: query.lang === 'all' ? null : (query.lang ?? null),
        q: query.q ?? null,
        cursor: query.cursor ?? null,
      })}`,
    ),

  messages: async (conversationId) =>
    (await call<{ messages: Message[] }>(`/conversations/${conversationId}/messages`)).messages,

  lead: (leadId) => call<Lead>(`/leads/${leadId}`),
  explain: (turnId) => call<Explain>(`/turns/${turnId}/explain`),

  sendMessage: (leadId, text) => call<Message>(`/leads/${leadId}/messages`, json({ text })),
  addNote: (leadId, body) => call<Lead>(`/leads/${leadId}/notes`, json({ body })),
  // MISMATCH 8: 409 on a backwards move. The error reaches the Pipeline drawer, which keeps the
  // card where it is and shows the API's sentence rather than snapping it back silently.
  setStage: (leadId, stage, reason) => call<Lead>(`/leads/${leadId}/stage`, json({ stage, reason })),

  handoffs: async (status = 'all') =>
    (await call<{ handoffs: Handoff[] }>(`/handoffs${qs({ status: status === 'all' ? null : status })}`)).handoffs,
  // MISMATCH 4: this IS the take-over. It writes `handoff.takeovers` and mutes the agent 20 min.
  claimHandoff: async (id) => (await call<{ handoff: Handoff }>(`/handoffs/${id}/claim`, post())).handoff,
  releaseHandoff: async (id) => (await call<{ handoff: Handoff }>(`/handoffs/${id}/release`, post())).handoff,
  closeHandoff: async (id, outcome: HandoffOutcome, reason) =>
    (await call<{ handoff: Handoff }>(`/handoffs/${id}/close`, json({ outcome, reason }))).handoff,

  pipeline: () => call<{ stages: PipelineStage[]; cards: PipelineCard[] }>('/pipeline'),
  // The cursor is what makes "Load more" load more; without it the route answers page one again
  // and keeps handing back a non-null `nextCursor`, so the button never stops offering itself.
  contacts: (query) =>
    call<Page<Contact>>(
      `/contacts${qs({ q: query.q ?? null, consent: query.consent ?? null, cursor: query.cursor ?? null })}`,
    ),

  products: async (query) =>
    (await call<{ products: Product[] }>(
      // `all` is the console's word for "no filter" and is not a category; sending it would
      // return an empty grid that looks like an empty catalogue.
      `/catalogue/products${qs({ q: query.q ?? null, category: query.category && query.category !== 'all' ? query.category : null })}`,
    )).products,
  product: async (id) => (await call<{ product: Product }>(`/catalogue/products/${id}`)).product,
  setMediaFlags: async (mediaId, flags) =>
    (await call<{ product: Product }>(`/catalogue/media/${mediaId}`, json(flags))).product,

  health: () => call<HealthSummary>('/health/summary'),
  people: async () => (await call<{ users: User[] }>('/people')).users,
  addPerson: (input: { email: string; name: string; role: Role }) =>
    call<{ user: User; password: string }>('/people', json(input)),
  deactivatePerson: async (id) => (await call<{ user: User }>(`/people/${id}/deactivate`, post())).user,
};
